import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../services/api'
import { useAuthStore } from '../store/auth.store'

interface RestaurantSummary {
  id: string
  name: string
  logoUrl: string | null
  slug: string
  isActive: boolean
  venueType: string
  colorTheme: string
  todayRevenue: number
  todayOrderCount: number
  activeOrders: number
}

interface SummaryResponse {
  totals: { todayRevenue: number; todayOrderCount: number; activeOrders: number }
  restaurants: RestaurantSummary[]
}

interface RestaurantStats {
  restaurantName: string
  period: string
  revenue: number
  orderCount: number
  topDishes: { name: string; count: number }[]
  revenueByDay: { date: string; total: number }[]
}

type Period = 'today' | 'week' | 'month'

const PERIOD_LABELS: Record<Period, string> = {
  today: 'Hoy',
  week: 'Últimos 7 días',
  month: 'Últimos 30 días',
}

export default function CorporatePage() {
  const groupName = useAuthStore((s) => s.groupName)
  const userName = useAuthStore((s) => s.userName)
  const clearAuth = useAuthStore((s) => s.clearAuth)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [period, setPeriod] = useState<Period>('today')

  const { data: summary, isLoading } = useQuery<SummaryResponse>({
    queryKey: ['corporate-summary'],
    queryFn: async () => {
      const { data } = await api.get('/corporate/summary')
      return data
    },
    refetchInterval: 30_000,
  })

  const { data: stats } = useQuery<RestaurantStats>({
    queryKey: ['corporate-stats', selectedId, period],
    queryFn: async () => {
      const { data } = await api.get(`/corporate/${selectedId}/stats?period=${period}`)
      return data
    },
    enabled: !!selectedId,
  })

  const selected = summary?.restaurants.find((r) => r.id === selectedId)

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-400 font-medium uppercase tracking-widest">Panel Corporativo</p>
          <h1 className="text-xl font-black text-gray-900">{groupName ?? 'Mi Grupo'}</h1>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-500">{userName}</span>
          <button
            onClick={clearAuth}
            className="text-sm text-gray-400 hover:text-red-500 transition-colors"
          >
            Cerrar sesión
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-8">

        {/* Totales del día */}
        {summary && (
          <div className="grid grid-cols-3 gap-4">
            <TotalCard label="Ingresos hoy" value={`$${summary.totals.todayRevenue.toFixed(2)}`} />
            <TotalCard label="Órdenes hoy" value={String(summary.totals.todayOrderCount)} />
            <TotalCard label="Órdenes activas" value={String(summary.totals.activeOrders)} highlight={summary.totals.activeOrders > 0} />
          </div>
        )}

        {/* Grid de sucursales */}
        <section>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-widest mb-4">Sucursales</h2>
          {isLoading && <p className="text-gray-400 text-sm">Cargando...</p>}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {summary?.restaurants.map((r) => (
              <button
                key={r.id}
                onClick={() => setSelectedId(r.id === selectedId ? null : r.id)}
                className={`text-left p-5 rounded-2xl border-2 transition-all ${
                  selectedId === r.id
                    ? 'border-gray-900 bg-white shadow-md'
                    : 'border-gray-100 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-3 mb-3">
                  {r.logoUrl
                    ? <img src={r.logoUrl} alt={r.name} className="w-10 h-10 rounded-full object-cover" />
                    : <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-lg font-black text-gray-400">{r.name[0]}</div>
                  }
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-gray-900 truncate">{r.name}</p>
                    <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium ${r.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                      {r.isActive ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <p className="text-xs text-gray-400">Ingresos</p>
                    <p className="text-sm font-bold text-gray-800">${r.todayRevenue.toFixed(0)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Órdenes</p>
                    <p className="text-sm font-bold text-gray-800">{r.todayOrderCount}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Activas</p>
                    <p className={`text-sm font-bold ${r.activeOrders > 0 ? 'text-orange-500' : 'text-gray-400'}`}>
                      {r.activeOrders}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </section>

        {/* Panel de detalle de sucursal seleccionada */}
        {selected && (
          <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <h2 className="text-lg font-black text-gray-900">{selected.name}</h2>
              <div className="flex gap-2">
                {(['today', 'week', 'month'] as Period[]).map((p) => (
                  <button
                    key={p}
                    onClick={() => setPeriod(p)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                      period === p ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {PERIOD_LABELS[p]}
                  </button>
                ))}
              </div>
            </div>

            {stats && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs text-gray-400 mb-1">Ingresos ({PERIOD_LABELS[period].toLowerCase()})</p>
                    <p className="text-2xl font-black text-gray-900">${stats.revenue.toFixed(2)}</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs text-gray-400 mb-1">Órdenes ({PERIOD_LABELS[period].toLowerCase()})</p>
                    <p className="text-2xl font-black text-gray-900">{stats.orderCount}</p>
                  </div>
                </div>

                {stats.topDishes.length > 0 && (
                  <div>
                    <p className="text-sm font-semibold text-gray-500 mb-3">Platillos más pedidos</p>
                    <div className="space-y-2">
                      {stats.topDishes.map((d, i) => (
                        <div key={d.name} className="flex items-center gap-3">
                          <span className="text-xs text-gray-400 w-5 text-right">{i + 1}</span>
                          <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
                            <div
                              className="h-2 rounded-full bg-gray-800"
                              style={{ width: `${(d.count / stats.topDishes[0].count) * 100}%` }}
                            />
                          </div>
                          <span className="text-sm text-gray-700 flex-1">{d.name}</span>
                          <span className="text-sm font-bold text-gray-900 w-8 text-right">{d.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {stats.revenueByDay.length > 0 && (
                  <div>
                    <p className="text-sm font-semibold text-gray-500 mb-3">Ingresos por día</p>
                    <RevenueChart data={stats.revenueByDay} />
                  </div>
                )}
              </>
            )}
          </section>
        )}
      </div>
    </div>
  )
}

function TotalCard({ label, value, highlight = false }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5">
      <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-1">{label}</p>
      <p className={`text-2xl font-black ${highlight ? 'text-orange-500' : 'text-gray-900'}`}>{value}</p>
    </div>
  )
}

function RevenueChart({ data }: { data: { date: string; total: number }[] }) {
  const max = Math.max(...data.map((d) => d.total), 1)
  return (
    <div className="flex items-end gap-1.5 h-24">
      {data.map((d) => (
        <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group">
          <div
            className="w-full bg-gray-800 rounded-t-sm transition-all group-hover:bg-gray-600"
            style={{ height: `${(d.total / max) * 80}px`, minHeight: 2 }}
            title={`${d.date}: $${d.total.toFixed(2)}`}
          />
          <span className="text-xs text-gray-300 truncate w-full text-center">
            {d.date.slice(5)}
          </span>
        </div>
      ))}
    </div>
  )
}
